import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase-auth';
import {
  FingerPrintIcon,
  KeyIcon,
  TrashIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  startRegistration
} from '../../lib/webauthn';

interface WebAuthnCredential {
  id: string;
  credential_id: string;
  credential_name: string;
  credential_type: string;
  authenticator_attachment?: string;
  created_at: string;
  last_used_at?: string;
  is_active: boolean;
}

export default function WebAuthnManagement() {
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [webauthnSupported, setWebauthnSupported] = useState(false);
  const [platformAvailable, setPlatformAvailable] = useState(false);

  useEffect(() => {
    checkWebAuthnSupport();
    fetchCredentials();
  }, []);

  const checkWebAuthnSupport = async () => {
    const supported = isWebAuthnSupported();
    setWebauthnSupported(supported);
    
    if (supported) {
      const platform = await isPlatformAuthenticatorAvailable();
      setPlatformAvailable(platform);
    }
  };

  const fetchCredentials = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('webauthn_credentials')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCredentials(data || []);
    } catch (err) {
      console.error('Error fetching WebAuthn credentials:', err);
      setError('Failed to load security keys');
    } finally {
      setLoading(false);
    }
  };

  const registerSecurityKey = async (type: 'platform' | 'cross-platform') => {
    setRegistering(true);
    setError(null);
    setSuccess(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      // Start WebAuthn registration
      const { credential, credentialData } = await startRegistration({
        user: {
          id: user.id,
          email: user.email!,
          name: user.email!.split('@')[0]
        },
        authenticatorType: type,
        userVerification: 'preferred'
      });

      if (!credential || !credentialData) {
        setError('Registration cancelled or failed');
        return;
      }

      // Save to database via edge function
      const { data, error: saveError } = await supabase.functions.invoke('webauthn-register', {
        body: credentialData
      });

      if (saveError || !data.success) {
        setError(data?.error || 'Failed to save security key');
        return;
      }

      setSuccess(`${type === 'platform' ? 'Passkey' : 'Security key'} registered successfully!`);
      await fetchCredentials();
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Registration was cancelled or not allowed');
      } else if (err.name === 'InvalidStateError') {
        setError('An authenticator is already registered');
      } else {
        setError('Failed to register security key');
      }
    } finally {
      setRegistering(false);
    }
  };

  const deleteCredential = async (credentialId: string) => {
    try {
      const { error } = await supabase
        .from('webauthn_credentials')
        .update({ is_active: false })
        .eq('id', credentialId);

      if (error) throw error;

      setSuccess('Security key removed successfully');
      await fetchCredentials();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting credential:', err);
      setError('Failed to remove security key');
    }
  };

  const getCredentialIcon = (credential: WebAuthnCredential) => {
    // Check if it's a platform authenticator (passkey)
    if (credential.authenticator_attachment === 'platform' || credential.credential_type === 'platform') {
      return <FingerPrintIcon className="h-6 w-6 text-blue-600" />;
    }
    return <KeyIcon className="h-6 w-6 text-green-600" />;
  };

  if (!webauthnSupported) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-medium text-yellow-900">
                Security Keys Not Supported
              </h3>
              <p className="mt-2 text-sm text-yellow-700">
                Your browser doesn't support WebAuthn security keys. Please use a modern browser like Chrome, Safari, Firefox, or Edge.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Security Keys & Passkeys</h3>
              <p className="mt-1 text-sm text-gray-500">
                Use hardware security keys or built-in biometrics for the strongest authentication
              </p>
            </div>
            <ShieldCheckIcon className="h-8 w-8 text-amber-500" />
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mx-6 mt-4 p-4 bg-green-50 rounded-md">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* Add New Security Key Options */}
        <div className="p-6 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-900 mb-4">Add New Security Method</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Platform Authenticator (Passkey) */}
            {platformAvailable && (
              <button
                onClick={() => registerSecurityKey('platform')}
                disabled={registering}
                className="relative rounded-lg border border-gray-300 bg-white p-6 shadow-sm hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <FingerPrintIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="ml-4 text-left">
                    <h5 className="text-base font-medium text-gray-900">
                      Add Passkey
                    </h5>
                    <p className="mt-1 text-sm text-gray-500">
                      Use Face ID, Touch ID, or Windows Hello
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Recommended
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Passwordless
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            )}

            {/* Security Key */}
            <button
              onClick={() => registerSecurityKey('cross-platform')}
              disabled={registering}
              className="relative rounded-lg border border-gray-300 bg-white p-6 shadow-sm hover:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <KeyIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4 text-left">
                  <h5 className="text-base font-medium text-gray-900">
                    Add Security Key
                  </h5>
                  <p className="mt-1 text-sm text-gray-500">
                    Use a YubiKey or other FIDO2 device
                  </p>
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      Hardware Key
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Registered Credentials */}
        <div className="px-6 py-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4">
            Registered Security Keys ({credentials.length})
          </h4>
          
          {credentials.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No security keys registered yet. Add one above for stronger security.
            </p>
          ) : (
            <div className="space-y-4">
              {credentials.map((credential) => (
                <div
                  key={credential.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getCredentialIcon(credential)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h5 className="text-sm font-medium text-gray-900">
                            {credential.credential_name}
                          </h5>
                          {(credential.authenticator_attachment === 'platform' || credential.credential_type === 'platform') && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Passkey
                            </span>
                          )}
                        </div>
                        
                        <div className="mt-1 text-xs text-gray-500 space-y-1">
                          <div>
                            Added {formatDistanceToNow(new Date(credential.created_at), { addSuffix: true })}
                          </div>
                          {credential.last_used_at && (
                            <div>
                              Last used {formatDistanceToNow(new Date(credential.last_used_at), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center ml-4">
                      {showDeleteConfirm === credential.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600">Remove?</span>
                          <button
                            onClick={() => deleteCredential(credential.id)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(null)}
                            className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowDeleteConfirm(credential.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="px-6 py-4 bg-blue-50 border-t border-blue-200 rounded-b-lg">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>About Security Keys:</strong> Passkeys and security keys provide the strongest protection against phishing and account takeover. 
                Passkeys use your device's built-in biometrics, while security keys are physical devices you plug in or tap.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}